import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Course, Instructor, Schedule } from '../types';
import TrackBadge from '../components/TrackBadge';
import { formatDate, getUpcomingScheduledSessions } from '../utils/dateUtils';
import PricingTable from '../components/PricingTable';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import { useAuthFetch } from '../hooks/useAuthFetch';

/** Initials avatar with image fallback */
const InstructorAvatar: React.FC<{ name: string; photoUrl?: string }> = ({ name, photoUrl }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  if (photoUrl && !imgFailed) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className="instructor-photo"
        onError={() => setImgFailed(true)}
      />
    );
  }

  return (
    <div className="instructor-avatar" aria-label={name}>
      {initials}
    </div>
  );
};

/** Safely coerce a field that should be an array but might be a string or undefined. */
function toArray(val: unknown): string[] {
  if (Array.isArray(val)) return val.filter((item): item is string => typeof item === 'string');
  if (typeof val === 'string' && val.trim()) return [val];
  return [];
}

const sanitize = (s: string) => s.replace(/\s*-{3,}\s*$/, '').trim();

const isAudienceIntro = (item: string, index: number) => {
  if (index !== 0) return false;

  const normalized = item.toLowerCase();
  const introStarts = [
    'this course is designed for',
    'this course is for',
    'designed for',
  ];

  const hasIntroStart = introStarts.some((prefix) => normalized.startsWith(prefix));
  const looksLikeSentence = item.includes('.');

  return hasIntroStart || (item.length > 140 && looksLikeSentence);
};

const CourseDetail: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const authFetch = useAuthFetch();
  const [course, setCourse] = useState<Course | null>(null);
  usePageTitle(course ? course.title : 'Course Details');
  const [instructor, setInstructor] = useState<Instructor | null>(null);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [enrolling, setEnrolling] = useState<string | null>(null);
  const [enrollSuccess, setEnrollSuccess] = useState<string | null>(null);
  const [enrollError, setEnrollError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const courseRes = await fetch(`/api/courses/${courseId}`);
        if (courseRes.ok) {
          const courseData: Course = await courseRes.json();
          setCourse(courseData);

          if (courseData.instructorId) {
            const instructorRes = await fetch(`/api/instructors/${courseData.instructorId}`);
            if (instructorRes.ok) {
              setInstructor(await instructorRes.json());
            }
          }
        }

        const schedulesRes = await fetch(`/api/schedules?courseId=${courseId}`);
        if (schedulesRes.ok) {
          const schedulesData: Schedule[] = await schedulesRes.json();
          setSchedules(getUpcomingScheduledSessions(schedulesData));
        }
      } catch (error) {
        console.error('Error fetching course:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading course details...</p>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="error-container">
        <h2>Course Not Found</h2>
        <p>The course you are looking for does not exist or has been removed.</p>
        <Link to="/courses" className="btn btn-primary">Back to Catalog</Link>
      </div>
    );
  }

  const handleEnroll = async (scheduleId: string) => {
    // If not authenticated, go straight to checkout
    if (!isAuthenticated || !user) {
      navigate(`/checkout?courseId=${courseId}&scheduleId=${scheduleId}`);
      return;
    }

    setEnrolling(scheduleId);
    setEnrollError(null);
    setEnrollSuccess(null);

    try {
      // Step 1: Check if user has an active subscription
      const subsRes = await authFetch('/api/subscriptions?status=active');
      if (!subsRes.ok) {
        // Can't check subscriptions — fall back to checkout
        navigate(`/checkout?courseId=${courseId}&scheduleId=${scheduleId}`);
        return;
      }

      const subscriptions = await subsRes.json();
      // Find a subscription where the user is the customer or on the student list,
      // and that has entitlement remaining (coursesRemaining is null = unlimited, or > 0)
      const eligible = subscriptions.find(
        (sub: any) =>
          sub.status === 'active' &&
          (sub.coursesRemaining === null || sub.coursesRemaining > 0)
      );

      if (!eligible) {
        // No active subscription with entitlement — redirect to checkout
        navigate(`/checkout?courseId=${courseId}&scheduleId=${scheduleId}`);
        return;
      }

      // Step 2: Attempt direct enrollment
      const enrollRes = await authFetch('/api/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scheduleId,
          studentName: user.name,
          studentEmail: user.email,
          subscriptionId: eligible.id,
        }),
      });

      if (enrollRes.ok) {
        await enrollRes.json(); // consume response body
        setEnrollSuccess(
          `You're enrolled! A confirmation email has been sent to ${user.email}.`
        );
        // Update the local schedule count so the UI reflects the new seat count
        setSchedules((prev) =>
          prev.map((s) =>
            s.id === scheduleId
              ? { ...s, currentEnrollment: s.currentEnrollment + 1 }
              : s
          )
        );
        return;
      }

      // Handle specific error codes from the API
      const errorData = await enrollRes.json().catch(() => ({}));

      if (
        errorData.errorCode === 'ENTITLEMENT_EXHAUSTED' ||
        errorData.errorCode === 'NO_SUBSCRIPTION' ||
        errorData.errorCode === 'SUBSCRIPTION_INACTIVE'
      ) {
        // Redirect to checkout to purchase/upgrade
        navigate(`/checkout?courseId=${courseId}&scheduleId=${scheduleId}`);
        return;
      }

      if (enrollRes.status === 409) {
        // Duplicate enrollment or fully booked
        setEnrollError(errorData.message || 'Unable to enroll. The session may be full or you may already be enrolled.');
        return;
      }

      // Other errors — show message
      setEnrollError(errorData.message || 'An error occurred during enrollment. Please try again.');
    } catch (error) {
      console.error('Enrollment error:', error);
      // Network error — fall back to checkout
      navigate(`/checkout?courseId=${courseId}&scheduleId=${scheduleId}`);
    } finally {
      setEnrolling(null);
    }
  };

  const handleSelectTier = (tier: 'Starter' | 'Pro' | 'Team') => {
    navigate(`/checkout?courseId=${courseId}&tier=${tier}`);
  };

  return (
    <div className="course-detail-page">
      {/* Header */}
      <section className="course-detail-header">
        <div className="section-container">
          <div className="breadcrumb">
            <Link to="/courses">Courses</Link>
            <span className="breadcrumb-sep">/</span>
            <span>{course.title}</span>
          </div>
          <div className="course-detail-title-row">
            <div>
              <TrackBadge track={course.track} size="large" />
              <h1 className="course-detail-title">{course.title}</h1>
              <p className="course-detail-description">{course.shortDescription}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course at a Glance */}
      <section className="section">
        <div className="section-container">
          <h2 className="section-title">Course at a Glance</h2>
          <div className="glance-grid">
            <div className="glance-item">
              <span className="glance-label">Duration</span>
              <span className="glance-value">{course.duration}</span>
            </div>
            <div className="glance-item">
              <span className="glance-label">Delivery</span>
              <span className="glance-value">{course.delivery}</span>
            </div>
            <div className="glance-item">
              <span className="glance-label">Skill Level</span>
              <span className="glance-value">{course.skillLevel}</span>
            </div>
            <div className="glance-item">
              <span className="glance-label">Lab Environment</span>
              <span className="glance-value">{course.labEnvironment}</span>
            </div>
            <div className="glance-item">
              <span className="glance-label">Certification Alignment</span>
              <span className="glance-value">
                {toArray(course.certificationAlignment).join(', ') || 'N/A'}
              </span>
            </div>
            <div className="glance-item">
              <span className="glance-label">Licensing</span>
              <span className="glance-value">{course.licensingRequirements || 'N/A'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Course */}
      {course.whyThisCourse && (
        <section className="section section-alt">
          <div className="section-container">
            <h2 className="section-title">Why This Course</h2>
            <div className="detail-content">
              <p>{sanitize(course.whyThisCourse)}</p>
            </div>
          </div>
        </section>
      )}

      {/* What You Will Learn */}
      {toArray(course.whatYouWillLearn).length > 0 && (
        <section className="section">
          <div className="section-container">
            <h2 className="section-title">What You Will Learn</h2>
            <ul className="learn-list">
              {toArray(course.whatYouWillLearn).map((item, index) => (
                <li key={index} className="learn-item">
                  <span className="learn-check">&#10003;</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Who Should Attend */}
      {toArray(course.whoShouldAttend).length > 0 && (() => {
        const sanitizedAudience = toArray(course.whoShouldAttend)
          .map(sanitize)
          .filter(Boolean);
        const hasIntro = sanitizedAudience.length > 0 && isAudienceIntro(sanitizedAudience[0], 0);
        const intro = hasIntro ? sanitizedAudience[0] : null;
        const audienceItems = hasIntro ? sanitizedAudience.slice(1) : sanitizedAudience;

        return (
        <section className="section section-alt">
          <div className="section-container">
            <h2 className="section-title">Who Should Attend</h2>
            {intro && <p className="audience-intro">{intro}</p>}
            <ul className="learn-list learn-list--audience">
              {audienceItems.map((item, index) => {
                const audienceItemClass = [
                  'learn-item',
                  'learn-item--audience',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <li key={index} className={audienceItemClass}>
                    <span className="learn-check">&#10003;</span>
                    <span>{item}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
        );
      })()}

      {/* Curriculum */}
      {(Array.isArray(course.curriculum) ? course.curriculum : []).length > 0 && (
        <section className="section">
          <div className="section-container">
            <h2 className="section-title">Curriculum</h2>
            <div className="curriculum-list">
              {(Array.isArray(course.curriculum) ? course.curriculum : []).map((module, index) => (
                <div key={index} className="curriculum-module">
                  <h3 className="module-title">
                    <span className="module-number">Module {index + 1}</span>
                    {module.title}
                  </h3>
                  <ul className="module-topics">
                    {(module.topics || []).map((topic, tIndex) => (
                      <li key={tIndex} className="module-topic">{topic}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Labs */}
      {(Array.isArray(course.labs) ? course.labs : []).length > 0 && (
        <section className="section section-alt">
          <div className="section-container">
            <h2 className="section-title">Hands-On Labs</h2>
            <div className="labs-grid">
              {(Array.isArray(course.labs) ? course.labs : []).map((lab, index) => (
                <div key={index} className="lab-card">
                  <h4 className="lab-title">
                    <span className="lab-number">Lab {index + 1}</span>
                    {lab.title}
                  </h4>
                  <p className="lab-description">{lab.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Differentiators */}
      {(Array.isArray(course.differentiators) ? course.differentiators : []).length > 0 && (
        <section className="section">
          <div className="section-container">
            <h2 className="section-title">What Sets This Course Apart</h2>
            <div className="differentiators-grid">
              {(Array.isArray(course.differentiators) ? course.differentiators : []).map((item: any, index: number) => {
                const isObj = typeof item === 'object' && item !== null;
                const title = isObj ? (item.title || item.description || JSON.stringify(item)) : item;
                const description = isObj && item.title && item.description ? item.description : null;
                return (
                  <div key={index} className="differentiator-item">
                    <div className="differentiator-header">
                      <span className="differentiator-icon">&#9733;</span>
                      <span className="differentiator-title">{title}</span>
                    </div>
                    {description && (
                      <p className="differentiator-description">{description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Prerequisites */}
      {toArray(course.prerequisites).length > 0 && (
        <section className="section section-alt">
          <div className="section-container">
            <h2 className="section-title">Prerequisites</h2>
            <ul className="prerequisites-list">
              {toArray(course.prerequisites).map((item, index) => (
                <li key={index} className="prerequisite-item">{item}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Instructor */}
      {instructor && (
        <section className="section">
          <div className="section-container">
            <h2 className="section-title">Your Instructor</h2>
            <div className="instructor-card">
              <InstructorAvatar name={instructor.name} photoUrl={instructor.photoUrl} />
              <div className="instructor-info">
                <h3 className="instructor-name">{instructor.name}</h3>
                <p className="instructor-title">{instructor.title}</p>
                <p className="instructor-bio">{instructor.bio}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Pricing */}
      <section className="section section-alt" id="pricing">
        <div className="section-container">
          <h2 className="section-title">Pricing</h2>
          <PricingTable onSelectTier={handleSelectTier} />
        </div>
      </section>

      {/* Upcoming Sessions */}
      <section className="section">
        <div className="section-container">
          <h2 className="section-title">Upcoming Sessions</h2>
          {enrollSuccess && (
            <div className="alert alert-success" style={{ marginBottom: '1rem', padding: '1rem', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px', color: '#155724' }}>
              {enrollSuccess}
            </div>
          )}
          {enrollError && (
            <div className="alert alert-error" style={{ marginBottom: '1rem', padding: '1rem', background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '6px', color: '#721c24' }}>
              {enrollError}
            </div>
          )}
          {schedules.length > 0 ? (
            <div className="sessions-table-wrapper">
              <table className="sessions-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Seats Available</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((schedule) => {
                    const seatsLeft = schedule.maxSeats - schedule.currentEnrollment;
                    return (
                      <tr key={schedule.id}>
                        <td>{formatDate(schedule.sessionDate, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</td>
                        <td>{schedule.startTime} - {schedule.endTime}</td>
                        <td>
                          <span className={`seats-indicator ${seatsLeft <= 3 ? 'seats-low' : ''}`}>
                            {seatsLeft} of {schedule.maxSeats}
                          </span>
                        </td>
                        <td>
                          <span className="status-badge status-scheduled">Open</span>
                        </td>
                        <td>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleEnroll(schedule.id)}
                            disabled={seatsLeft <= 0 || enrolling === schedule.id}
                          >
                            {seatsLeft <= 0 ? 'Sold Out' : enrolling === schedule.id ? 'Enrolling...' : 'Enroll'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No upcoming sessions scheduled. Check back soon or contact us for custom scheduling.</p>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      {(Array.isArray(course.faqs) ? course.faqs : []).length > 0 && (
        <section className="section section-alt">
          <div className="section-container">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <div className="faq-list">
              {(Array.isArray(course.faqs) ? course.faqs : []).map((faq, index) => (
                <div
                  key={index}
                  className={`faq-item ${expandedFaq === index ? 'faq-expanded' : ''}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <span>{faq.question}</span>
                    <span className="faq-toggle">{expandedFaq === index ? '\u2212' : '+'}</span>
                  </button>
                  {expandedFaq === index && (
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default CourseDetail;
